import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';

import { useUserState } from '../../SupporterVoices/hooks/useUserState';
import type { MapConfiguration } from '../shared/types';
import { isSessionStaff } from '../shared/utils/isSessionStaff';
import { mapConfiguration } from '../shared/utils/mapConfiguration';
import { mapShouldBeHidden } from '../shared/utils/mapShouldBeHidden';

type ShouldSeeTheMapResult =
	| {
			shouldSeeTheMap: true;
			userNotInVariantGroup?: undefined;
			petitionIsTooOld?: undefined;
			petitionIsBlocked?: undefined;
			countNotInRange?: undefined;
			isStaff: boolean;
			configuration: MapConfiguration;
	  }
	| {
			shouldSeeTheMap: false;
			userNotInVariantGroup?: boolean;
			petitionIsTooOld?: boolean;
			petitionIsBlocked?: boolean;
			countNotInRange?: boolean;
			isStaff?: undefined;
			configuration?: undefined;
	  };

export function useShouldTheUserSeeTheMap(): ShouldSeeTheMapResult {
	const {
		data: {
			petition: {
				id,
				createdAt,
				country: { countryCode },
				signatureState: {
					signatureCount: { displayed },
				},
			},
			featureConfigs: { signaturesDistributionConfig },
		},
	} = usePetitionDetailsPageData();
	const userState = useUserState();

	if (countryCode !== 'US') {
		return { shouldSeeTheMap: false };
	}
	if (!signaturesDistributionConfig) {
		return { shouldSeeTheMap: false };
	}

	const { activatedPercentage, petitions, createdAfter, blocklist, signatureCount } = signaturesDistributionConfig;
	const { session } = userState;

	const { hideMap, petitionIsTooOld, petitionIsBlocked, userNotInVariantGroup, countNotInRange } = mapShouldBeHidden({
		session,
		id,
		activatedPercentage,
		actualSignatureCount: displayed,
		signatureCountRange: signatureCount,
		createdAt,
		createdAfter,
		petitions,
		blocklist,
	});
	if (hideMap) {
		return {
			shouldSeeTheMap: false,
			petitionIsTooOld,
			petitionIsBlocked,
			userNotInVariantGroup,
			countNotInRange,
		};
	}

	return {
		shouldSeeTheMap: true,
		isStaff: isSessionStaff(session),
		configuration: mapConfiguration(
			signaturesDistributionConfig,
			petitions.find(({ petitionId }) => petitionId === id),
		),
	};
}
